import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getToken } from "services/auth.service";
import {
  clearAuthorizationCache,
  getAuthorization,
  normalizeAuthorization,
  readAuthorizationCache,
  writeAuthorizationCache,
} from "./authorization.service";

const AuthorizationContext = createContext(null);

export function AuthorizationProvider({ children, eager = false }) {
  const [authorization, setAuthorization] = useState(() => readAuthorizationCache());
  const [loading, setLoading] = useState(eager);
  const [error, setError] = useState(null);

  const refreshAuthorization = useCallback(async () => {
    if (!getToken()) {
      setAuthorization(null);
      setLoading(false);
      clearAuthorizationCache();
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const data = normalizeAuthorization(await getAuthorization());
      setAuthorization(data);
      writeAuthorizationCache(data);
      return data;
    } catch (requestError) {
      setError(requestError);
      throw requestError;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (eager) {
      refreshAuthorization().catch(() => { /* Error is retained in provider state; consumers keep access closed. */ });
    }
  }, [eager, refreshAuthorization]);

  useEffect(() => {
    const handleAuthorizationChanged = (event) => {
      setAuthorization(event.detail ? normalizeAuthorization(event.detail) : null);
    };

    window.addEventListener("authorization:changed", handleAuthorizationChanged);
    return () => window.removeEventListener("authorization:changed", handleAuthorizationChanged);
  }, []);

  const permissionsSet = useMemo(
    () => new Set((authorization?.permissions || []).map((permission) => String(permission).toLowerCase())),
    [authorization],
  );

  const roleCodesSet = useMemo(
    () =>
      new Set(
        (authorization?.roles || [])
          .map((role) => role.roleCode ?? role.RoleCode ?? role)
          .filter(Boolean)
          .map((roleCode) => String(roleCode).toLowerCase()),
      ),
    [authorization],
  );

  const applicationCodesSet = useMemo(
    () =>
      new Set(
        (authorization?.applications || [])
          .flatMap((application) => [
            application.applicationCode ?? application.ApplicationCode,
            application.accessPermissionCode ?? application.AccessPermissionCode,
          ])
          .filter(Boolean)
          .map((applicationCode) => String(applicationCode).toLowerCase()),
      ),
    [authorization],
  );

  const can = useCallback(
    (permissionCode) => {
      if (!permissionCode) return false;
      return permissionsSet.has(String(permissionCode).toLowerCase());
    },
    [permissionsSet],
  );

  const canAny = useCallback(
    (permissionCodes = []) => {
      return permissionCodes.some((permissionCode) => can(permissionCode));
    },
    [can],
  );

  const canAll = useCallback(
    (permissionCodes = []) => {
      return permissionCodes.every((permissionCode) => can(permissionCode));
    },
    [can],
  );

  const hasRole = useCallback(
    (roleCode) => roleCodesSet.has(String(roleCode || "").toLowerCase()),
    [roleCodesSet],
  );

  const hasApplication = useCallback(
    (applicationCode) => applicationCodesSet.has(String(applicationCode || "").toLowerCase()),
    [applicationCodesSet],
  );

  const clearAuthorization = useCallback(() => {
    setAuthorization(null);
    setError(null);
    clearAuthorizationCache();
  }, []);

  const value = useMemo(
    () => ({
      authorization,
      user: authorization?.user || null,
      roles: authorization?.roles || [],
      permissions: authorization?.permissions || [],
      applications: authorization?.applications || [],
      isAdmin: Boolean(authorization?.isAdmin),
      loading,
      error,
      can,
      canAny,
      canAll,
      hasRole,
      hasApplication,
      refreshAuthorization,
      clearAuthorization,
    }),
    [
      authorization,
      loading,
      error,
      can,
      canAny,
      canAll,
      hasRole,
      hasApplication,
      refreshAuthorization,
      clearAuthorization,
    ],
  );

  return <AuthorizationContext.Provider value={value}>{children}</AuthorizationContext.Provider>;
}

export function useAuthorization() {
  const context = useContext(AuthorizationContext);
  if (!context) {
    throw new Error("useAuthorization must be used inside AuthorizationProvider");
  }
  return context;
}

export default AuthorizationContext;
